import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

const formTitle = (form) =>
  form?.type === "client-details"
    ? "Client details form"
    : form?.type === "check-in"
    ? "Check-in form"
    : "Submitted form";

const textOf = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value).trim();
};

/** Fixed order matching the form, so the answers read the way they were filled in. */
const FIELD_LABELS = [
  ["name", "Name"],
  ["number", "Phone"],
  ["email", "Email"],
  ["address", "Address"],
  ["australianAddress", "Australian address"],
  ["homeCountryAddress", "Home country address"],
  ["nationality", "Nationality"],
  ["gender", "Gender"],
  ["dob", "Date of birth"],
  ["maritalStatus", "Marital status"],
  ["passportNumber", "Passport number"],
  ["dateOfIssue", "Passport issue date"],
  ["passportExpiry", "Passport expiry date"],
  ["usiNumber", "USI number"],
  ["visaStatus", "Visa status"],
  ["visaExpiry", "Visa expiry"],
  ["emergencyContactName", "Emergency contact name"],
  ["emergencyRelation", "Emergency relation"],
  ["emergencyContact", "Emergency contact number"],
  ["emergencyEmail", "Emergency email"],
  ["intake", "Intake"],
  ["currentCourseProvider", "Current course provider"],
  ["interestedServices", "Interested services"],
  ["source", "Source"],
  ["sourceOther", "Source (other)"],
  ["comments", "Comments"],
  ["notes", "Notes"],
  ["summary", "Summary"],
  ["discussion", "Discussion"],
  ["followUpDate", "Follow-up date"],
  ["status", "Status"],
];

const META_KEYS = new Set([
  "id",
  "createdDate",
  "updatedDate",
  "convertedClientId",
  "clientConversionStatus",
  "extraFieldsJson",
  "key1",
  "key2",
  "key3",
  "key4",
  "key5",
  "type",
]);

const extraRows = (form) => {
  if (!form?.extraFieldsJson) return [];
  try {
    const parsed = JSON.parse(form.extraFieldsJson);
    if (!parsed || typeof parsed !== "object") return [];
    return Object.entries(parsed)
      .filter(([key]) => !META_KEYS.has(key))
      .map(([key, value]) => [key, textOf(value)])
      .filter(([, value]) => value);
  } catch {
    return [];
  }
};

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b border-app-border last:border-0">
    <div className="w-44 shrink-0 text-sm text-app-text-muted">{label}</div>
    <div className="text-sm font-medium break-words whitespace-pre-wrap min-w-0">{value}</div>
  </div>
);

/**
 * What the client (or the front desk) filled in on the check-in / client-details form, exactly
 * as stored. Read-only: corrections happen on the client profile or in the form itself.
 */
const ClientSourceFormDialog = ({ form, isLoading, open, onClose }) => {
  const rows = form
    ? [
        ...FIELD_LABELS.map(([key, label]) => [label, textOf(form[key])]).filter(
          ([, value]) => value
        ),
        ...extraRows(form),
      ]
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {formTitle(form)}
        {form?.createdDate ? (
          <Typography variant="body2" color="text.secondary">
            Submitted {new Date(form.createdDate).toLocaleString()}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <CircularProgress size={24} />
          </div>
        ) : !form ? (
          <Typography color="error">Could not load the submitted form.</Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">The submitted form has no answers.</Typography>
        ) : (
          rows.map(([label, value]) => <Row key={label} label={label} value={value} />)
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientSourceFormDialog;
